<template>
  <div class="article-slot__wrapper">
    <article-slot-header
      :link-to-article="linkToArticle"
      :title="title"
      :is-migrated="isMigrated"
      :tldr="tldr"
      :title-html-tag="titleHtmlTag"
      :slug="slug"
      :category="category"
      :featured-image="featuredImage"
      :thumbnail-image="thumbnailImage"
      :featured-video="featuredVideo"
      :show-featured-media="showFeaturedMedia"
      :breakpoints-to-show-featured-media-on="breakpointsToShowFeaturedMediaOn"
      :image-viewport-optimization="imageViewportOptimization"
      :hide-category="hideCategory"
      :expanded-content="expandedContent"
      :author="author"
      :published-on="publishedOn"
      :sponsorship-company="sponsorshipCompany"
      :breakpoints-to-show-expandable-content-on="
        breakpointsToShowExpandableContentOn
      "
      :show-expandable-content="showExpandableContent"
      :is-content-expanded="isContentExpanded"
      :is-pinned="isPinned"
      :show-pin="showPin"
      :article-slot-style="articleSlotStyle"
      @expanded-changed="handledExpandedChanged"
    />

    <a-visibility show :on="[$breakpoint.mobile, $breakpoint.tablet]">
      <article-slot-info
        :author="author"
        :published-on="publishedOn"
        :sponsorship-company="sponsorshipCompany"
        :link-to-article="linkToArticle"
        :expanded-content="expandedContent"
        :show-expandable-content="showExpandableContent"
        :is-content-expanded="isContentExpanded"
        :breakpoints-to-show-expandable-content-on="
          breakpointsToShowExpandableContentOn
        "
        @expanded-changed="handledExpandedChanged"
      />
    </a-visibility>
  </div>
</template>

<script>
import { PROP_TYPES, propValidator } from '@fmpedia/validators'
import { HTML_TAG } from '@/utils/helpers/processHtml/enums'
import { generateServerCacheKey } from '@/plugins/helper'
import mixins from '@/utils/mixins'
import ArticleSlotInfo from 'shared/AArticleSlot/ArticleSlotInfo'
import ArticleSlotHeader from 'shared/AArticleSlot/ArticleSlotHeader/index.vue'
import scss from '@/utils/scss'
import { ARTICLE_SLOT_STYLE } from 'shared/AArticleSlot/enums'

export default {
  name: 'AArticleSlot',
  serverCacheKey: ({
    modifiedOn,
    articleId,
    category,
    hideCategory,
    isPinned,
    featuredImage,
    thumbnailImage,
    featuredVideo,
    author,
    sponsorshipCompany,
    hidePublisher,
    slotStyle,
    expandable,
    titleHtmlTag,
    showFeaturedMedia,
    showExpandableContent,
    breakpointsToShowFeaturedImageOn,
    breakpointsToShowExpandableContentOn,
    imageViewportOptimization,
    showPin
  }) => {
    const categoryModifiedCacheKeyPart = category
      ? `${category.name}-${category.slug}`
      : ''

    const image = thumbnailImage || featuredImage

    const imageModifiedCacheKeyPart = image
      ? `${image.altText}-${image.caption}-${image.title}`
      : ''

    const authorModifiedCacheKeyPart = author
      ? `${author.name}-${author.photoUrl}-${author.slug}`
      : ''

    function generateLinkedCompanyCacheKey({
      name,
      slug,
      categorySlug,
      logoUrl,
      isActive
    }) {
      return `${name}-${slug}-${categorySlug}-${logoUrl}-${isActive}`
    }

    const companyModifiedCacheKeyPart =
      sponsorshipCompany && sponsorshipCompany.linkedCompany
        ? generateLinkedCompanyCacheKey(sponsorshipCompany.linkedCompany)
        : ''

    const otherFieldsCacheKeyPart = [
      showExpandableContent,
      showFeaturedMedia,
      (breakpointsToShowFeaturedImageOn || []).join('-'),
      (breakpointsToShowExpandableContentOn || []).join('-'),
      imageViewportOptimization,
      hideCategory,
      isPinned,
      hidePublisher,
      slotStyle || '',
      expandable,
      titleHtmlTag,
      featuredVideo?.ExternalId || '',
      showPin
    ].join('-')

    const resultingServerCacheKey = [
      articleId,
      modifiedOn,
      categoryModifiedCacheKeyPart,
      imageModifiedCacheKeyPart,
      authorModifiedCacheKeyPart,
      companyModifiedCacheKeyPart,
      otherFieldsCacheKeyPart
    ].join('-')

    return generateServerCacheKey(resultingServerCacheKey)
  },
  mixins: [mixins.urlGenerators],
  components: {
    ArticleSlotHeader,
    ArticleSlotInfo
  },
  props: {
    articleId: propValidator([PROP_TYPES.STRING]),
    title: propValidator([PROP_TYPES.STRING]),
    slug: propValidator([PROP_TYPES.STRING]),
    isMigrated: propValidator([PROP_TYPES.BOOLEAN], false, false),
    isSessionWrap: propValidator([PROP_TYPES.BOOLEAN]),
    sponsorshipCompany: propValidator([PROP_TYPES.OBJECT], false),
    category: propValidator([PROP_TYPES.OBJECT], false),
    tldr: propValidator([PROP_TYPES.STRING], false),
    isPinned: propValidator([PROP_TYPES.BOOLEAN], false, false),
    featuredImage: propValidator([PROP_TYPES.OBJECT], false),
    thumbnailImage: propValidator([PROP_TYPES.OBJECT], false),
    featuredVideo: propValidator([PROP_TYPES.OBJECT], false),
    author: propValidator([PROP_TYPES.OBJECT], false),
    expandedContent: propValidator([PROP_TYPES.STRING], false),
    terms: propValidator([PROP_TYPES.ARRAY], false),
    publishedOn: propValidator([PROP_TYPES.STRING]),
    modifiedOn: propValidator([PROP_TYPES.STRING]),
    titleHtmlTag: propValidator(
      [PROP_TYPES.STRING],
      false,
      HTML_TAG.HEADING_H3
    ),
    showFeaturedMedia: propValidator([PROP_TYPES.BOOLEAN], false, true),
    breakpointsToShowFeaturedImageOn: propValidator(
      [PROP_TYPES.ARRAY],
      false,
      () => Object.values(scss.breakpoints)
    ),
    showExpandableContent: propValidator([PROP_TYPES.BOOLEAN], false, true),
    breakpointsToShowExpandableContentOn: propValidator(
      [PROP_TYPES.ARRAY],
      false,
      () => Object.values(scss.breakpoints)
    ),
    imageViewportOptimization: propValidator(
      [PROP_TYPES.BOOLEAN],
      false,
      false
    ),
    hideCategory: propValidator([PROP_TYPES.BOOLEAN], false, false),
    showPin: propValidator([PROP_TYPES.BOOLEAN], false, true),
    articleSlotStyle: propValidator(
      [PROP_TYPES.STRING],
      false,
      ARTICLE_SLOT_STYLE.LARGE
    )
  },
  data() {
    return {
      isContentExpanded: false
    }
  },
  computed: {
    linkToArticle() {
      return this.generateArticlePath({
        categorySlug: this.category.slug,
        slug: this.slug,
        isMigrated: this.isMigrated
      })
    },
    breakpointsToShowFeaturedMediaOn() {
      if (this.featuredVideo) {
        return [
          this.$breakpoint.desktopSm,
          this.$breakpoint.desktopMd,
          this.$breakpoint.desktopLg,
          this.$breakpoint.desktopXl,
          this.$breakpoint.aboveDesktopXl
        ]
      } else {
        return this.breakpointsToShowFeaturedImageOn
      }
    }
  },
  methods: {
    handledExpandedChanged(isExpanded) {
      this.isContentExpanded = isExpanded
    }
  },
  mounted() {
    this.$emit('mounted')
  }
}
</script>

<style lang="scss" scoped>
.article-slot__wrapper {
  padding-bottom: 24px;
  border-bottom: 1px solid $c--gray-light;

  /deep/ .article-info__wrapper {
    padding-top: 24px;
  }
}
</style>
